import { adminConflicts, invasionPolicies } from '../data/world/invasionPolicies'
import { nameKey } from './worldFormat'
import type { InvasionPolicy, ItemCategory } from '../data/world/invasionPolicies'
import type { DropRoute, RouteTarget, WorldData } from '../types/world'

/*
 * Validación compartida: contrasta cada ruta exportada que relaciona un ítem con una invasión
 * con la política aprobada. La usan las fichas de ítem, de monstruo, el diagnóstico y el buscador
 * (a través de las fichas). Las rutas no autorizadas se retienen para los jugadores y se
 * conservan en el panel técnico.
 */

export type InvasionVerdict = 'authorized' | 'conflict' | 'unlisted'

export type InvasionCheck = {
  policy: InvasionPolicy
  verdict: InvasionVerdict
  reason: string
}

const cache = new WeakMap<WorldData, Map<string, InvasionCheck[]>>()

export function itemCategory(section: number | undefined, name: string): ItemCategory {
  if (/^jewel of /i.test(name) || /^gemstone$/i.test(name)) return 'jewel'
  if (section === undefined) return 'other'
  if (section >= 0 && section <= 6) return 'weapon'
  if (section >= 7 && section <= 11) return 'set'
  return 'other'
}

const categoryLabel: Record<ItemCategory, string> = { weapon: 'arma', set: 'pieza de set', jewel: 'joya', other: 'otro tipo de ítem' }

function policyMatchesTarget(p: InvasionPolicy, t: RouteTarget): boolean {
  if (t.eventId) return p.eventIds.includes(t.eventId)
  return t.monsterId !== undefined && p.monsterIds.includes(t.monsterId)
}

/** Políticas de invasión que afectan a una ruta (por bag_id, event_id o monster_id confirmados). */
export function policiesForRoute(w: WorldData, r: DropRoute): InvasionPolicy[] {
  const targets = w.idx.routeTargetsByRoute.get(r.id) ?? []
  return invasionPolicies.filter((p) => (r.bagId !== undefined && p.bagIds.includes(r.bagId)) || targets.some((t) => policyMatchesTarget(p, t)))
}

function evaluate(w: WorldData, r: DropRoute, p: InvasionPolicy): InvasionCheck {
  const itemId = r.itemId ?? ''
  const item = w.items.get(itemId)
  const variant = r.variantId ? w.variants.get(r.variantId) : undefined
  const name = variant?.name ?? item?.name ?? itemId
  const baseName = item?.name ?? name
  const quality = (r.quality ?? '').toLowerCase()
  const isExc = quality === 'excellent' || quality === 'ancient'

  const admin = adminConflicts.find((c) => c.guideEntryId === p.guideEntryId && c.itemId === itemId)
  if (admin) return { policy: p, verdict: 'conflict', reason: admin.note }

  if (p.authorizedItemIds?.includes(itemId) || p.authorizedNames.some((n) => nameKey(n) === nameKey(baseName) || nameKey(n) === nameKey(name))) {
    if (isExc && p.forbidExcellent) return { policy: p, verdict: 'conflict', reason: `Versión ${quality}: la información aprobada excluye ítems Excellent/Ancient.` }
    return { policy: p, verdict: 'authorized', reason: `Nombrado expresamente en la información aprobada: ${p.approved}` }
  }

  // Sin ficha editorial aprobada (p. ej. Golden): pendiente de aprobación, no contradicción entre fuentes.
  if (!p.generalCategories.length && !p.authorizedNames.length) {
    return { policy: p, verdict: 'unlisted', reason: 'Pendiente de aprobación editorial: la invasión aún no tiene ficha aprobada en la guía. Los datos exportados existen y se conservan.' }
  }

  if (isExc && p.forbidExcellent) return { policy: p, verdict: 'conflict', reason: `Versión ${quality}: la información aprobada indica NO Excellent.` }

  const cat = itemCategory(item?.section, baseName)
  if (!p.generalCategories.includes(cat)) {
    return {
      policy: p,
      verdict: 'conflict',
      reason: p.generalCategories.length || p.authorizedNames.length
        ? `${categoryLabel[cat]} no incluido en las recompensas aprobadas (${p.approved})`
        : 'La invasión no tiene información aprobada en la guía.',
    }
  }
  if (p.excellentOnlyGeneral && cat !== 'jewel' && !isExc) {
    return { policy: p, verdict: 'conflict', reason: 'La información aprobada solo contempla un ítem Excellent adicional del jefe; esta versión no es Excellent.' }
  }
  const lvl = variant?.level
  if (p.levels?.length && lvl !== undefined && !p.levels.includes(lvl)) {
    return { policy: p, verdict: 'conflict', reason: `Nivel +${lvl} fuera de lo aprobado (+${p.levels.join('/+')}).` }
  }
  return { policy: p, verdict: 'unlisted', reason: `Encaja en la descripción general (${categoryLabel[cat]}), pero no hay lista vigente que lo confirme. Falta: ${p.missing ?? 'lista vigente de ítems'}` }
}

/** Resultado de todas las políticas para una ruta. Vacío = la ruta no es de invasión. */
export function invasionChecks(w: WorldData, r: DropRoute): InvasionCheck[] {
  let m = cache.get(w)
  if (!m) {
    m = new Map()
    cache.set(w, m)
  }
  const hit = m.get(r.id)
  if (hit) return hit
  const out = policiesForRoute(w, r).map((p) => evaluate(w, r, p))
  m.set(r.id, out)
  return out
}

/** Políticas NO autorizadas para la ruta: sus objetivos se retienen para los jugadores. */
export function withheldPolicies(w: WorldData, r: DropRoute): InvasionCheck[] {
  return invasionChecks(w, r).filter((c) => c.verdict !== 'authorized')
}

/** ¿Este objetivo pertenece a una invasión cuya asociación está retenida? */
export function targetWithheld(w: WorldData, r: DropRoute, t: { eventId?: string; monsterId?: number }): boolean {
  const held = withheldPolicies(w, r)
  if (!held.length) return false
  if (held.some((c) => r.bagId !== undefined && c.policy.bagIds.includes(r.bagId))) return true
  return held.some((c) => (t.eventId ? c.policy.eventIds.includes(t.eventId) : t.monsterId !== undefined && c.policy.monsterIds.includes(t.monsterId)))
}

export type InvasionAudit = {
  policy: InvasionPolicy
  routes: number
  items: number
  authorized: number
  unlisted: number
  conflict: number
  rows: { routeId: string; itemId: string; name: string; quality?: string; verdict: InvasionVerdict; reason: string }[]
}

/** Auditoría completa para el panel técnico. */
export function auditInvasions(w: WorldData): InvasionAudit[] {
  const by = new Map<string, InvasionAudit>()
  invasionPolicies.forEach((p) => by.set(p.guideEntryId, { policy: p, routes: 0, items: 0, authorized: 0, unlisted: 0, conflict: 0, rows: [] }))
  w.routes.forEach((r) => {
    invasionChecks(w, r).forEach((c) => {
      const a = by.get(c.policy.guideEntryId)!
      a.routes++
      a[c.verdict]++
      const id = r.variantId ?? r.itemId ?? ''
      a.rows.push({ routeId: r.id, itemId: id, name: w.variants.get(id)?.name ?? w.items.get(r.itemId ?? '')?.name ?? id, quality: r.quality, verdict: c.verdict, reason: c.reason })
    })
  })
  by.forEach((a) => (a.items = new Set(a.rows.map((x) => x.itemId.split('@')[0])).size))
  return Array.from(by.values())
}
